import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import dayjs from "dayjs";
import styles from "@/components/appointmentArchive/appointmentArchiveTable.module.css";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { getTranslatedStatusOptions } from "@/helper/statusOptions";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import DateRangeFilterModal, {
  type DateRangeFilterModalValue,
} from "@/components/dateTime/dateRangeFilterModal";
import DateRangeFilterChip from "@/components/dateTime/dateRangeFilterChip";

const AppointmentArchiveTable: FC = () => {
  const { t } = useTranslation();
  usePageHeading(t("appointment_archive"));
  const [startDate, setStartDate] = useState<any>(null);
  const [endDate, setEndDate] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [additionalParametersForFilter, setAdditionalParametersForFilter] =
    useState<any>();
  const statusFilterOptions = getTranslatedStatusOptions("workerAppointments");
  const columns = [
    {
      header: t("id"),
      id: "id",
      accessorKey: "id",
      enableHiding: true,
      visibleInShowHideMenu: false,
    },
    {
      header: t("date"),
      accessorKey: "datetime",
      id: "datetime",
      filterVariant: "date",
      sortingFn: "date",
      accessorFn: (row: any) => {
        if (row.datetime) {
          return (
            dayjs(row.datetime).format("DD.MM.YYYY") +
            " - " +
            `${dayjs(row.datetime).format("HH:mm") + " " + t("time_indicator")
            } `
          );
        } else {
          return "-";
        }
      },
    },
    {
      header: t("examination"),
      accessorKey: "examination_set",
      id: "examination",
      customColumnFilterId: "examination_set__name",
      customSortingKey: "examination_name",
      accessorFn: (row: any) => {
        if (row.examination_set &&
          Array.isArray(row.examination_set) &&
          row.examination_set.length > 0
        ) {
          return row.examination_set.map((exam: any) => exam.name).join(", ");
        }
        return "";
      },
    },
    {
      header: t("department"),
      accessorKey: "subject.department_set",
      id: "departmentName",
      customColumnFilterId: "department_name",
      customSortingKey: "department_set__name",
      accessorFn: (row: any) => {
        const departmentSet = row.subject?.department_set;
        const departments = [];
        for (const key in departmentSet) {
          const name = departmentSet[key].name;
          departments.push(name);
        }
        return departments.join(", ");
      },
    },
    {
      header: t("status"),
      accessorKey: "status",
      filterVariant: "select",
      filterSelectOptions: statusFilterOptions,
      id: "status",
      accessorFn: (row: any) => {
        const status = row.status;
        const string = "status_" + status;
        return `${t(string)}`;
      },
    },
    {
      header: t("building_location"),
      accessorKey: "building_information.city",
      id: "building_information__name",
      customColumnFilterId: "building_information__building__name",
      customSortingKey: "building_name",
      childEntityName: "building_information",
      accessorFn: (row: any) => {
        const parts = [];
        const { name, street_house, zip, city } = row.building_information;
        if (name) {
          parts.push(name);
        }
        if (street_house) {
          parts.push(street_house);
        }
        if (zip || city) {
          const zipCity = [zip, city].filter(Boolean).join(" ");
          parts.push(zipCity);
        }
        return parts.join(", ");
      },
    },
  ];

  const updateDateRangeFilter = (dateRange: DateRangeFilterModalValue) => {
    const [nextStartDate, nextEndDate] = dateRange;
    const additionalUrlParameters: any = {};
    if (nextStartDate !== null && nextEndDate !== null) {
      additionalUrlParameters["datetime_date_range"] = [
        dayjs(nextStartDate).format("YYYY-MM-DD"),
        dayjs(nextEndDate).format("YYYY-MM-DD"),
      ];
    }
    setStartDate(nextStartDate);
    setEndDate(nextEndDate);
    setAdditionalParametersForFilter(additionalUrlParameters);
  };

  const applyDateRange = (dateRange: DateRangeFilterModalValue) => {
    updateDateRangeFilter(dateRange);
    setModalOpen(false);
  };

  const clearDateRangeFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setAdditionalParametersForFilter(null);
  };

  const handleClearDate = (e: any) => {
    e.stopPropagation();
    clearDateRangeFilter();
  };

  const navigateDateRange = (direction: "left" | "right") => {
    if (startDate === null || endDate === null) return;

    const rangeLengthInDays = dayjs(endDate).diff(dayjs(startDate), "day") + 1;
    const offset = direction === "left" ? -rangeLengthInDays : rangeLengthInDays;

    updateDateRangeFilter([
      dayjs(startDate).add(offset, "day").toDate(),
      dayjs(endDate).add(offset, "day").toDate(),
    ]);
  };

  const customToolbarButton = () => {
    return (
      <div className={styles.toolbarButtonContainer}>
        <GenericButton onClick={() => setModalOpen(true)}>
          {t("period")}
        </GenericButton>

        {startDate !== null && endDate !== null && (
          <DateRangeFilterChip
            filterLabel={t("date")}
            startDate={startDate}
            endDate={endDate}
            onDelete={handleClearDate}
            onClick={() => setModalOpen(true)}
            onNavigateLeft={() => navigateDateRange("left")}
            onNavigateRight={() => navigateDateRange("right")}
          />
        )}
      </div>
    );
  };

  return (
    <>
      <GenericCrudTable
        entity="appointment/worker_appointments"
        columns={columns}
        showRowActions={false}
        additionalUrlParameter={additionalParametersForFilter}
        customTopToolBarAction={() => customToolbarButton()}
        hasAdditionalFiltersToReset={startDate !== null || endDate !== null}
        onResetAllFilters={clearDateRangeFilter}
        disableAddItem={true}
        allowColumnFiltering={true}
        showColumnFilterbyDefault={true}
      />
      {modalOpen && (
        <DateRangeFilterModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          title={t("choose_period")}
          initialDateRange={[startDate, endDate]}
          onApply={applyDateRange}
        />
      )}
    </>
  );
};

export default AppointmentArchiveTable;
